import { useState } from 'react'
import { download, query } from '../../api/client'
import { useQuery } from '../../api/hooks'
import type { AgingReport, ClientFundNature, PartyType } from '../../api/types'
import { useAuth } from '../../auth/AuthContext'
import { P } from '../../auth/permissions'
import { Empty, ErrorBanner, Loading, Money, Panel, isoDate } from '../../components/ui'

export function AgingPage() {
  const { can } = useAuth()
  const [asOf, setAsOf] = useState(isoDate(new Date()))
  const [type, setType] = useState<PartyType>('Client')
  const [nature, setNature] = useState<ClientFundNature | ''>('')

  const report = useQuery<AgingReport>(
    `/reports/aging${query({ asOf, type, nature })}`,
    [asOf, type, nature],
  )

  return (
    <>
      <div className="toolbar" style={{ marginBottom: 14 }}>
        <div className="field">
          <label>As at</label>
          <input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} />
        </div>

        <div className="field">
          <label>Party type</label>
          <select value={type} onChange={(e) => setType(e.target.value as PartyType)}>
            <option value="Client">Clients</option>
            <option value="Vendor">Vendors</option>
            <option value="Employee">Employees</option>
          </select>
        </div>

        {type === 'Client' && (
          <div className="field">
            <label>Nature</label>
            <select value={nature} onChange={(e) => setNature(e.target.value as ClientFundNature | '')}>
              <option value="">All</option>
              <option value="Fee">Fees</option>
              <option value="Custody">Custody</option>
              <option value="Settlement">Settlements</option>
            </select>
          </div>
        )}

        <div className="spacer" />

        {can(P.reports.export) && (
          <button
            onClick={() => void download(`/reports/aging/excel${query({ asOf, type, nature })}`, 'aging.xlsx')}
          >
            Export to Excel
          </button>
        )}
      </div>

      <ErrorBanner error={report.error} />
      {report.loading && <Loading />}

      {report.data && (
        <Panel flush>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 90 }}>Code</th>
                  <th>Name</th>
                  <th className="num">Current</th>
                  <th className="num">1–30</th>
                  <th className="num">31–60</th>
                  <th className="num">61–90</th>
                  <th className="num">Over 90</th>
                  <th className="num">Total</th>
                </tr>
              </thead>
              <tbody>
                {report.data.rows.length === 0 && (
                  <tr>
                    <td colSpan={8}>
                      <Empty>Nothing outstanding.</Empty>
                    </td>
                  </tr>
                )}

                {report.data.rows.map((row) => (
                  <tr key={row.partyId}>
                    <td className="code">{row.partyCode}</td>
                    <td>{row.partyName}</td>
                    <td className="num">
                      <Money value={row.current} blankZero />
                    </td>
                    <td className="num">
                      <Money value={row.days1To30} blankZero />
                    </td>
                    <td className="num">
                      <Money value={row.days31To60} blankZero />
                    </td>
                    <td className="num">
                      <Money value={row.days61To90} blankZero />
                    </td>
                    {/* Over 90 days is the column anyone chasing debts actually reads. */}
                    <td className={row.over90 > 0 ? 'num negative strong' : 'num'}>
                      <Money value={row.over90} blankZero />
                    </td>
                    <td className="num strong">
                      <Money value={row.total} />
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={2}>Total</td>
                  <td className="num">
                    <Money value={report.data.totals.current} />
                  </td>
                  <td className="num">
                    <Money value={report.data.totals.days1To30} />
                  </td>
                  <td className="num">
                    <Money value={report.data.totals.days31To60} />
                  </td>
                  <td className="num">
                    <Money value={report.data.totals.days61To90} />
                  </td>
                  <td className="num">
                    <Money value={report.data.totals.over90} />
                  </td>
                  <td className="num">
                    <Money value={report.data.totals.total} />
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div className="muted" style={{ padding: '10px 14px', fontSize: 12 }}>
            Outstanding, not billed — a receipt entered against a document reduces it here. Money
            taken on account without naming a document settles nothing and leaves these figures
            unchanged.
          </div>
        </Panel>
      )}
    </>
  )
}
